import React from 'react'

import { LeftImageContent, NoImageContent, RightImageContent, TopImageContent } from '..'
import { SectionProps } from '../../../../core/types/Help'
import { Text, SectionTitle, List } from '../ContentItem'
import { HELP_IMAGES } from '../../../constants/HelpImages'
import { buildDynamicCopy } from '../../../../core/utils/Localization'

const Debts: React.FC<SectionProps> = ({ copy }) => {
  const copyContent = copy.debts
  const debtsSections = {
    overviewSection: (
      <TopImageContent
        altImage={copyContent.overview_image_alt}
        content={
          <Text
            content={
              buildDynamicCopy(
                copyContent.overview_desc,
                <b>{copyContent.snowball_method}</b>,
              ) as React.ReactNode
            }
          />
        }
        image={HELP_IMAGES.DEBTS_OVERVIEW}
        imageLargeBreakPoint={7}
        sx={{ pt: 24 }}
      />
    ),
    snowballMethod: (
      <RightImageContent
        altImage={copyContent.snowball_method}
        content={<Text content={copyContent.snowball_method_desc} sx={{ mt: 24 }} />}
        image={HELP_IMAGES.DEBTS_SNOWBALL_METHOD}
        imageLargeBreakPoint={2}
      />
    ),
    debtPrioritization: (
      <LeftImageContent
        altImage={copyContent.debt_prioritization}
        content={
          <React.Fragment>
            <Text content={copyContent.debt_prioritization_list_1_desc} />
            <List
              items={[
                buildDynamicCopy(
                  copyContent.debt_prioritization_list_1_item_1,
                  <b>{copyContent.debt_prioritization_list_1_item_1_bold}</b>,
                ) as React.ReactNode,
                buildDynamicCopy(
                  copyContent.debt_prioritization_list_1_item_2,
                  <b>{copyContent.debt_prioritization_list_1_item_2_bold}</b>,
                ) as React.ReactNode,
                buildDynamicCopy(
                  copyContent.debt_prioritization_list_1_item_3,
                  <b>{copyContent.debt_prioritization_list_1_item_3_bold}</b>,
                ) as React.ReactNode,
                buildDynamicCopy(
                  copyContent.debt_prioritization_list_1_item_3,
                  <b>{copyContent.debt_prioritization_list_1_item_3_bold}</b>,
                ) as React.ReactNode,
              ]}
            />
            <Text content={copyContent.debt_prioritization_list_2_desc} sx={{ mt: 12 }} />
            <List
              items={[
                copyContent.debt_prioritization_list_2_item_1,
                copyContent.debt_prioritization_list_2_item_2,
              ]}
              listType="ol"
            />
          </React.Fragment>
        }
        image={HELP_IMAGES.DEBTS_PRIORITIZATION}
        imageLargeBreakPoint={4}
      />
    ),
    debtChart: (
      <NoImageContent
        content={
          <React.Fragment>
            <Text content={copyContent.debt_chart_list_dec} />
            <List
              items={[
                buildDynamicCopy(
                  copyContent.debt_chart_list_item_1,
                  <b>{copyContent.debt_chart_list_item_1_bold}</b>,
                ) as React.ReactNode,
                buildDynamicCopy(
                  copyContent.debt_chart_list_item_2,
                  <b>{copyContent.debt_chart_list_item_2_bold}</b>,
                ) as React.ReactNode,
                buildDynamicCopy(
                  copyContent.debt_chart_list_item_3,
                  <b>{copyContent.debt_chart_list_item_3_bold}</b>,
                ) as React.ReactNode,
              ]}
            />
            <Text content={copyContent.debt_chart_dec_2} sx={{ mt: 12 }} />
          </React.Fragment>
        }
      />
    ),
    debtList: (
      <NoImageContent
        content={
          <React.Fragment>
            <Text content={copyContent.debt_list_dec} />
            <List
              items={[
                copyContent.debt_list_item_1,
                copyContent.debt_list_item_2,
                copyContent.debt_list_item_3,
                copyContent.debt_list_item_4,
              ]}
            />
          </React.Fragment>
        }
      />
    ),
    debtDetails: (
      <LeftImageContent
        altImage={copyContent.debt_details}
        content={
          <React.Fragment>
            <Text content={copyContent.debt_details_list_1_desc} sx={{ mt: 24 }} />
            <List
              items={[
                copyContent.debt_details_list_1_item_1,
                buildDynamicCopy(
                  copyContent.debt_details_list_1_item_2,
                  <b>{copyContent.debt_details_list_1_item_2_bold}</b>,
                ) as React.ReactNode,
              ]}
              listType="ol"
            />
            <Text content={copyContent.debt_details_list_2_desc} sx={{ mt: 12 }} />
            <List
              items={[
                copyContent.debt_details_list_2_item_1,
                buildDynamicCopy(
                  copyContent.debt_details_list_2_item_2,
                  <b>{copyContent.debt_details_list_2_item_2_bold}</b>,
                ) as React.ReactNode,
                copyContent.debt_details_list_2_item_3,
              ]}
              listType="ol"
            />
          </React.Fragment>
        }
        image={HELP_IMAGES.DEBTS_DETAILS}
      />
    ),
    hidingAccounts: (
      <NoImageContent
        content={
          <React.Fragment>
            <Text content={copyContent.hiding_accounts_desc} />
            <Text content={copyContent.hiding_accounts_list_1_desc} sx={{ mt: 12 }} />
            <List
              items={[
                copyContent.hiding_accounts_list_1_item_1,
                copyContent.hiding_accounts_list_1_item_2,
                buildDynamicCopy(
                  copyContent.hiding_accounts_list_1_item_3,
                  <b>{copyContent.hiding_accounts_list_1_item_3_bold}</b>,
                ) as React.ReactNode,
              ]}
              listType="ol"
            />
            <Text content={copyContent.hiding_accounts_list_2_desc} sx={{ mt: 12 }} />
            <List
              items={[
                copyContent.hiding_accounts_list_2_item_1,
                buildDynamicCopy(
                  copyContent.hiding_accounts_list_2_item_2,
                  <b>{copyContent.hiding_accounts_list_2_item_2_bold}</b>,
                ) as React.ReactNode,
              ]}
              listType="ol"
            />
          </React.Fragment>
        }
      />
    ),
    extraPayments: (
      <NoImageContent
        content={
          <React.Fragment>
            <Text content={copyContent.extra_payments_list_1_desc} />
            <List
              items={[
                buildDynamicCopy(
                  copyContent.extra_payments_list_2_item_1,
                  <b>{copyContent.extra_payments_list_2_item_1_bold}</b>,
                ) as React.ReactNode,
                buildDynamicCopy(
                  copyContent.extra_payments_list_2_item_2,
                  <b>{copyContent.extra_payments_list_2_item_2_bold}</b>,
                ) as React.ReactNode,
              ]}
              listType="ol"
            />
          </React.Fragment>
        }
        hasDivider={false}
      />
    ),
  }

  return (
    <React.Fragment>
      {debtsSections.overviewSection}
      <SectionTitle content={copyContent.snowball_method_title} sx={{ pt: 48 }} />
      {debtsSections.snowballMethod}
      <SectionTitle content={copyContent.debt_prioritization} sx={{ pt: 48 }} />
      {debtsSections.debtPrioritization}
      <SectionTitle content={copyContent.debt_chart} sx={{ pt: 48 }} />
      {debtsSections.debtChart}
      <SectionTitle content={copyContent.debt_list} sx={{ pt: 48 }} />
      {debtsSections.debtList}
      <SectionTitle content={copyContent.debt_details} sx={{ pt: 48 }} />
      {debtsSections.debtDetails}
      <SectionTitle content={copyContent.hiding_accounts} sx={{ pt: 48 }} />
      {debtsSections.hidingAccounts}
      <SectionTitle content={copyContent.extra_payments} sx={{ pt: 48 }} />
      {debtsSections.extraPayments}
    </React.Fragment>
  )
}

export default Debts
